import { defaultManufacturingSeason } from "@/lib/manufacturingSeasons";
import { productCodeWithSeason } from "@/lib/manufacturingDuplicate";
import { SeasonSelect } from "@/components/ui/season-select";
import React from "react";
import { Check, RefreshCw, Save, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import {
  createManufacturingProductFromSalesProduct,
  fetchFamilies,
  fetchManufacturingProducts,
  fetchSalesProductsForManufacturing,
  fetchSeasons,
  formatMoney,
  getNextFamilyProductCode,
  LEGACY_SALE_PRICE_FIELDS,
  describeFamilyTemplateSeed,
  publishManufacturingProductToSales,
  saveManufacturingProduct,
} from "@/lib/api/manufacturing";

const emptyForm = {
  code: "",
  description: "",
  cod_fam: "",
  temporada: "",
  composicion: "",
  sizesText: "",
  colorsText: "",
  status: "active",
  precio_v: 0,
  precio_v1: 0,
  precio_v2: 0,
  precio_v3: 0,
  precio_v4: 0,
  precio_v5: 0,
};

const salePriceLabels = ["Tarifa 1", "Tarifa 2", "Tarifa 3", "Tarifa 4", "Tarifa 5", "Tarifa 6"];

function parseList(value) {
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function formatProduct(product) {
  if (!product) return "";
  return `${product.code} - ${product.description}`;
}

export function ManufacturingProductSelector({
  value,
  onSelect,
  allowPublish = false,
  allowCreate = true,
  initialStatus = "active",
}) {
  const [products, setProducts] = React.useState([]);
  const [families, setFamilies] = React.useState([]);
  const [seasons, setSeasons] = React.useState([]);
  const [salesProducts, setSalesProducts] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [salesSearchTerm, setSalesSearchTerm] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState(initialStatus);
  const [showNewForm, setShowNewForm] = React.useState(false);
  const [formData, setFormData] = React.useState(emptyForm);
  const [loading, setLoading] = React.useState(false);
  const [seasonsLoading, setSeasonsLoading] = React.useState(true);
  const [codeLoading, setCodeLoading] = React.useState(false);
  const codeRequestIdRef = React.useRef(0);
  const creationRef = React.useRef(null);
  const creatingRef = React.useRef(false);
  const { toast } = useToast();

  const selectedProduct = products.find((product) => product.id === value) || null;

  const loadProducts = React.useCallback(async () => {
    setLoading(true);
    const result = await fetchManufacturingProducts({ searchTerm, status: statusFilter });
    if (result.error) {
      toast({ title: "Error", description: "No se pudieron cargar productos de fabricacion", variant: "destructive" });
    }
    setProducts(result.data || []);
    setLoading(false);
  }, [searchTerm, statusFilter, toast]);

  const loadSalesProducts = React.useCallback(async () => {
    if (!allowCreate || !showNewForm) return;
    const result = await fetchSalesProductsForManufacturing(salesSearchTerm);
    if (!result.error) setSalesProducts(result.data || []);
  }, [allowCreate, showNewForm, salesSearchTerm]);

  const loadFamilies = React.useCallback(async () => {
    if (!allowCreate) return;
    const result = await fetchFamilies();
    if (result.error) {
      toast({ title: "Error", description: "No se pudieron cargar las familias", variant: "destructive" });
      return;
    }
    setFamilies(result.data || []);
  }, [allowCreate, toast]);

  const loadSeasons = React.useCallback(async () => {
    if (!allowCreate) return;
    setSeasonsLoading(true);
    const result = await fetchSeasons();
    setSeasonsLoading(false);
    if (result.error) {
      toast({ title: "Error", description: "No se pudieron cargar las temporadas", variant: "destructive" });
      return;
    }
    setSeasons(result.data || []);
  }, [allowCreate, toast]);

  React.useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  React.useEffect(() => {
    loadSalesProducts();
  }, [loadSalesProducts]);

  React.useEffect(() => {
    loadFamilies();
  }, [loadFamilies]);

  React.useEffect(() => {
    loadSeasons();
  }, [loadSeasons]);

  const generateManualProductCode = async (codFam, season = formData.temporada) => {
    const requestId = codeRequestIdRef.current + 1;
    codeRequestIdRef.current = requestId;

    if (!codFam || !season) {
      setFormData((current) => ({ ...current, code: "" }));
      setCodeLoading(false);
      return;
    }

    setCodeLoading(true);
    const result = await getNextFamilyProductCode(codFam, season);
    if (requestId !== codeRequestIdRef.current) return;
    setCodeLoading(false);

    if (result.error) {
      toast({
        title: "Error",
        description: result.error.message || "No se pudo generar el codigo",
        variant: "destructive",
      });
      setFormData((current) => ({ ...current, code: "" }));
      return;
    }

    setFormData((current) => ({ ...current, code: result.data || "" }));
  };

  const changeManualProductFamily = async (codFam) => {
    setFormData((current) => ({ ...current, cod_fam: codFam, code: "" }));
    await generateManualProductCode(codFam);
  };

  const changeManualProductSeason = async (season) => {
    setFormData((current) => ({ ...current, temporada: season,
      code: productCodeWithSeason(current.code, season, current.temporada) }));
    codeRequestIdRef.current += 1;
    setCodeLoading(false);
    if (!formData.code || codeLoading) await generateManualProductCode(formData.cod_fam, season);
  };

  const resetManualForm = () => {
    creationRef.current = null;
    codeRequestIdRef.current += 1;
    setFormData({ ...emptyForm, temporada: defaultManufacturingSeason(seasons) });
    setCodeLoading(false);
  };

  const toggleNewForm = () => {
    if (showNewForm) {
      resetManualForm();
      setShowNewForm(false);
      return;
    }
    resetManualForm();
    setShowNewForm(true);
  };

  const submitManualProduct = async (event) => {
    event.preventDefault();
    if (!formData.cod_fam.trim()) {
      toast({ title: "Validacion", description: "Selecciona una familia para generar el codigo", variant: "destructive" });
      return;
    }
    if (!formData.temporada.trim()) {
      toast({ title: "Validacion", description: "La temporada es obligatoria", variant: "destructive" });
      return;
    }
    if (codeLoading) {
      toast({ title: "Validacion", description: "Espera a que se genere el codigo automatico", variant: "destructive" });
      return;
    }
    if (!formData.code.trim() || !formData.description.trim()) {
      toast({ title: "Validacion", description: "Codigo y descripcion son obligatorios", variant: "destructive" });
      return;
    }
    if (formData.temporada.trim().length > 4) {
      toast({ title: "Validacion", description: "El codigo de temporada no puede superar 4 caracteres", variant: "destructive" });
      return;
    }

    if (creatingRef.current) return;
    creatingRef.current = true;
    creationRef.current ||= crypto.randomUUID();
    const result = await saveManufacturingProduct({
      code: formData.code,
      description: formData.description,
      cod_fam: formData.cod_fam,
      temporada: formData.temporada,
      composicion: formData.composicion,
      sizes: parseList(formData.sizesText),
      colors: parseList(formData.colorsText),
      status: formData.status,
      sync_to_sales: false,
      ...Object.fromEntries(LEGACY_SALE_PRICE_FIELDS.map((field) => [field, formData[field]])),
    }, null, { requestId: creationRef.current });
    creatingRef.current = false;

    if (result.error) {
      toast({ title: "Error", description: result.error.message || "No se pudo crear el producto", variant: "destructive" });
      return;
    }

    toast({
      title: "Producto de fabricacion creado",
      description: describeFamilyTemplateSeed(result) || undefined,
    });
    resetManualForm();
    setShowNewForm(false);
    await loadProducts();
    onSelect?.(result.data?.id);
  };

  const importSalesProduct = async (product) => {
    const result = await createManufacturingProductFromSalesProduct(product);
    if (result.error) {
      toast({ title: "Error", description: result.error.message || "No se pudo importar el producto", variant: "destructive" });
      return;
    }
    toast({ title: "Producto comercial vinculado a fabricacion" });
    resetManualForm();
    setShowNewForm(false);
    await loadProducts();
    onSelect?.(result.data?.id);
  };

  const publishSelected = async () => {
    if (!value) return;
    const result = await publishManufacturingProductToSales(value);
    if (result.error) {
      toast({ title: "Error", description: result.error.message || "No se pudo publicar en ventas", variant: "destructive" });
      return;
    }
    toast({ title: "Producto sincronizado con ventas" });
    await loadProducts();
  };

  return (
    <div className="min-w-0 space-y-3">
      <div className="flex min-w-0 flex-wrap gap-2">
        <Input
          type="search"
          placeholder="Buscar producto de fabricacion..."
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          className="min-w-[8rem] flex-1"
        />
        <select
          className="min-w-[7.5rem] flex-1 rounded-md border border-input bg-background px-3 py-2 sm:max-w-[10rem] sm:flex-none"
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
        >
          <option value="active">Activos</option>
          <option value="obsolete">Obsoletos</option>
          <option value="all">Todos</option>
        </select>
        {allowCreate && (
          <Button type="button" variant="outline" className="shrink-0" onClick={toggleNewForm} disabled={seasonsLoading && !showNewForm}>
            {showNewForm ? <X className="mr-2 h-4 w-4" /> : <Save className="mr-2 h-4 w-4" />}
            {showNewForm ? "Cerrar" : "Nuevo"}
          </Button>
        )}
      </div>

      <select
        className="w-full min-w-0 max-w-full rounded-md border border-input bg-background px-3 py-2"
        value={value || ""}
        onChange={(event) => onSelect?.(event.target.value || null)}
      >
        <option value="">{loading ? "Cargando..." : "Selecciona producto de fabricacion"}</option>
        {products.map((product) => (
          <option key={product.id} value={product.id}>
            {formatProduct(product)}
            {product.sales_product_id ? " · vinculado ventas" : ""}
          </option>
        ))}
      </select>

      {selectedProduct && (
        <div className="flex flex-col gap-2 rounded border bg-gray-50 p-3 text-sm md:flex-row md:items-center md:justify-between">
          <div>
            <div className="font-medium">{formatProduct(selectedProduct)}</div>
            <div className="text-gray-600">
              {selectedProduct.status} · {selectedProduct.season?.descrip || selectedProduct.temporada || "sin temporada"}
              {selectedProduct.family?.descrip ? ` · ${selectedProduct.family.descrip}` : ""}
              {` · Tarifa 1 ${formatMoney(selectedProduct.precio_v)}`}
              {selectedProduct.last_synced_at ? ` · sincronizado ${new Date(selectedProduct.last_synced_at).toLocaleDateString("es-ES")}` : ""}
            </div>
          </div>
          {allowPublish && (
            <Button type="button" variant="outline" size="sm" onClick={publishSelected}>
              <Send className="mr-2 h-4 w-4" />
              Publicar en ventas
            </Button>
          )}
        </div>
      )}

      {allowCreate && showNewForm && (
        <div className="grid gap-4 rounded border bg-white p-4 lg:grid-cols-2">
          <form onSubmit={submitManualProduct} className="space-y-3">
            <h3 className="font-semibold">Crear producto de fabricacion</h3>
            <select
              required
              className="w-full rounded-md border border-input bg-background px-3 py-2"
              value={formData.cod_fam}
              onChange={(event) => changeManualProductFamily(event.target.value)}
            >
              <option value="">Selecciona familia</option>
              {families.map((family) => (
                <option key={family.cod_fam} value={family.cod_fam.trim()}>
                  {family.cod_fam.trim()} · {family.descrip}
                </option>
              ))}
            </select>
            <div className="flex gap-2">
              <Input
                readOnly
                placeholder={codeLoading ? "Generando codigo..." : "Codigo automatico"}
                value={formData.code}
                className="bg-gray-50 font-mono"
              />
              <Button
                type="button"
                variant="outline"
                disabled={!formData.cod_fam || !formData.temporada || codeLoading}
                onClick={() => generateManualProductCode(formData.cod_fam)}
              >
                Regenerar
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              {formData.cod_fam && formData.temporada
                ? codeLoading
                  ? "Generando codigo automatico..."
                  : formData.code
                    ? `Codigo automatico asignado: ${formData.code}`
                    : "No se pudo asignar un codigo automatico."
                : "Selecciona familia y temporada para asignar el código automáticamente. Al guardar se generará el escandallo con su plantilla."}
            </p>
            <Input placeholder="Descripcion" value={formData.description} onChange={(event) => setFormData({ ...formData, description: event.target.value })} />
            <div className="grid gap-2 md:grid-cols-2">
              <SeasonSelect data-season-selector="true"
                required
                className="w-full rounded-md border border-input bg-background px-3 py-2"
                value={formData.temporada}
                onChange={(event) => changeManualProductSeason(event.target.value)}
              >
                <option value="">Selecciona temporada</option>
                {seasons.map((season) => (
                  <option key={season.cod_tem} value={season.cod_tem.trim()}>
                    {season.cod_tem.trim()} · {season.descrip}
                  </option>
                ))}
              </SeasonSelect>
              <Input placeholder="Composicion" value={formData.composicion} onChange={(event) => setFormData({ ...formData, composicion: event.target.value })} />
            </div>
            <div className="grid gap-2 md:grid-cols-2">
              <Input placeholder="Tallas separadas por coma" value={formData.sizesText} onChange={(event) => setFormData({ ...formData, sizesText: event.target.value })} />
              <Input placeholder="Colores separados por coma" value={formData.colorsText} onChange={(event) => setFormData({ ...formData, colorsText: event.target.value })} />
            </div>
            <div className="grid gap-2 md:grid-cols-3">
              {LEGACY_SALE_PRICE_FIELDS.map((field, index) => (
                <Input
                  key={field}
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder={salePriceLabels[index]}
                  value={formData[field]}
                  onChange={(event) => setFormData({ ...formData, [field]: event.target.value })}
                />
              ))}
            </div>
            <select
              className="w-full rounded-md border border-input bg-background px-3 py-2"
              value={formData.status}
              onChange={(event) => setFormData({ ...formData, status: event.target.value })}
            >
              <option value="active">Activo</option>
              <option value="obsolete">Obsoleto</option>
            </select>
            <Button type="submit" disabled={codeLoading}>
              <Check className="mr-2 h-4 w-4" />
              {codeLoading ? "Generando codigo..." : "Crear"}
            </Button>
          </form>

          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold">Crear desde ventas</h3>
              <Button type="button" variant="ghost" size="sm" onClick={loadSalesProducts}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
            <Input
              type="search"
              placeholder="Buscar en catalogo comercial..."
              value={salesSearchTerm}
              onChange={(event) => setSalesSearchTerm(event.target.value)}
            />
            <div className="max-h-64 space-y-2 overflow-auto">
              {salesProducts.map((product) => (
                <div key={product.id} className="flex items-center justify-between gap-3 rounded border p-2">
                  <div className="min-w-0">
                    <div className="truncate font-medium">{formatProduct(product)}</div>
                    <div className="text-sm text-gray-600">{product.temporada || "sin temporada"}</div>
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={() => importSalesProduct(product)}>
                    Importar
                  </Button>
                </div>
              ))}
              {salesProducts.length === 0 && <p className="text-sm text-gray-500">Sin resultados.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
